// Featured-series detail block from the mockup. Two columns on desktop:
// left = 16:9 still + Watch-trailer overlay, right = tags + title + rating +
// synopsis + episode picker + bundle CTA.
//
// On mobile stacks vertically, still on top, metadata below.

import { Link } from "react-router";
import { Play, Lock, Star, Share2 } from "lucide-react";
import { Badge, Button, cn } from "@bloom/ui";

export interface FeaturedSeriesData {
  _id: string;
  slug: string;
  titleTh: string;
  titleEn: string;
  taglineTh: string | null;
  taglineEn: string | null;
  synopsisTh: string;
  synopsisEn: string;
  posterUrl: string;
  categories: string[];
  episodeCount: number;
  year: number | null;
  ageRating: string | null;
  ratingScore: number | null;
  ratingCount: number | null;
  freeEpisodeCount: number | null;
  bundlePriceThb: number | null;
}

export interface FeaturedSeriesEpisode {
  _id: string;
  episodeNumber: number;
  titleTh: string;
  titleEn: string;
  durationSec: number;
  thumbnailUrl: string;
}

export interface FeaturedSeriesBlockProps {
  series: FeaturedSeriesData;
  episodes: FeaturedSeriesEpisode[];
  locale: "th" | "en";
  /** Per-episode unit price in THB. Defaults to 9. */
  episodePriceThb?: number;
  /** Override for the still image. Defaults to series.posterUrl. */
  stillUrl?: string;
}

const CATEGORY_LABELS: Record<string, { th: string; en: string }> = {
  bl: { th: "วาย", en: "BL" },
  drama: { th: "ดราม่า", en: "Drama" },
  romance: { th: "โรแมนติก", en: "Romance" },
  school: { th: "วัยเรียน", en: "School-age" },
  sport: { th: "กีฬา", en: "Sport" },
};

export function FeaturedSeriesBlock({
  series,
  episodes,
  locale,
  episodePriceThb = 9,
  stillUrl,
}: FeaturedSeriesBlockProps) {
  const title = locale === "th" ? series.titleTh : series.titleEn;
  const synopsis = locale === "th" ? series.synopsisTh : series.synopsisEn;
  const freeCount = series.freeEpisodeCount ?? 3;
  const bundlePrice = series.bundlePriceThb ?? 99;
  const tagline = locale === "th" ? series.taglineTh : series.taglineEn;
  const stillSrc = stillUrl ?? series.posterUrl;
  const eps = episodes.slice(0, 6);

  return (
    <section className="overflow-hidden rounded-3xl bg-[var(--brand-ink-soft)] text-[var(--brand-paper)] shadow-2xl">
      <div className="grid gap-0 md:grid-cols-[1fr_1fr]">
        {/* Left — landscape still + Watch trailer overlay */}
        <div className="relative aspect-[16/10] w-full overflow-hidden md:aspect-auto md:h-full">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <img src={stillSrc} alt={title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
          <Link
            to={`/series/${series.slug}#trailer`}
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-[var(--brand-ink)] hover:bg-white"
          >
            <Play className="h-4 w-4 fill-current" aria-hidden />{" "}
            {locale === "th" ? "ดูตัวอย่าง" : "Watch trailer"}
          </Link>
        </div>

        {/* Right — metadata + episode picker */}
        <div className="flex flex-col gap-4 p-6 md:p-8">
          <div className="flex flex-wrap gap-2">
            {series.categories.slice(0, 4).map((cat) => {
              const label = CATEGORY_LABELS[cat]?.[locale] ?? cat;
              return (
                <Badge
                  key={cat}
                  variant="rank"
                  className="bg-[var(--brand-pink)] text-[var(--brand-ink)]"
                >
                  {label}
                </Badge>
              );
            })}
          </div>
          <h2 className="font-display text-4xl leading-tight">{title}</h2>
          {tagline ? <p className="text-sm text-white/70">{tagline}</p> : null}

          <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/80">
            {series.ratingScore !== null ? (
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-current text-[var(--brand-rose)]" aria-hidden />
                <span className="font-semibold">{series.ratingScore.toFixed(1)}</span>
                {series.ratingCount ? (
                  <span className="text-white/60">({formatCount(series.ratingCount)})</span>
                ) : null}
              </div>
            ) : null}
            {series.year ? <span className="text-white/60">{series.year}</span> : null}
            {series.ageRating ? <span className="text-white/60">{series.ageRating}</span> : null}
          </dl>

          <p className="line-clamp-2 text-sm leading-relaxed text-white/80">{synopsis}</p>

          <Link
            to={`/series/${series.slug}`}
            className="text-sm font-semibold text-[var(--brand-rose)] hover:underline"
          >
            {locale === "th"
              ? `ดูฟรี ${freeCount} ตอนแรก →`
              : `Watch first ${freeCount} free episodes →`}
          </Link>

          {/* Episode picker */}
          <ul className="mt-2 divide-y divide-white/10 rounded-2xl bg-white/5">
            {eps.map((ep) => {
              const free = ep.episodeNumber <= freeCount;
              return (
                <li key={ep._id}>
                  <Link
                    to={`/watch/${series._id}/${ep._id}`}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-white/5"
                  >
                    <span className="flex items-center gap-2 font-mono text-xs text-white/80">
                      {free ? (
                        <Play
                          className="h-3.5 w-3.5 fill-current text-[var(--brand-rose)]"
                          aria-hidden
                        />
                      ) : (
                        <Lock className="h-3.5 w-3.5 text-white/60" aria-hidden />
                      )}
                      EP.{ep.episodeNumber}
                    </span>
                    <span className="text-xs text-white/70">
                      {free ? (locale === "th" ? "ฟรี" : "Free") : `${episodePriceThb} ฿`}
                    </span>
                  </Link>
                </li>
              );
            })}
            <li>
              <Link
                to={`/series/${series.slug}`}
                className="block px-4 py-2.5 text-center text-xs font-semibold text-white/70 hover:text-white"
              >
                {locale === "th"
                  ? `ดูทั้งหมด ${series.episodeCount} ตอน`
                  : `View all ${series.episodeCount} episodes`}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Bundle CTA strip */}
      <div className="flex flex-col items-stretch gap-3 border-t border-white/10 bg-[var(--brand-pink)] p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="text-[var(--brand-ink)]">
          <p className="font-heading text-base font-bold">
            {locale === "th"
              ? `เหมาทั้งซีรีส์ ${bundlePrice} บาท`
              : `Unlock the bundle — ${bundlePrice} THB`}
          </p>
          <p className="text-xs text-[var(--brand-ink)]/70">
            {locale === "th" ? "ดูได้ทุกตอน ไม่จำกัด" : "Watch every episode, unlimited"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/unlock/bundle/${series._id}`}
            className="inline-flex h-11 items-center justify-center rounded-full bg-[var(--brand-ink)] px-6 text-sm font-semibold text-[var(--brand-paper)]"
          >
            {locale === "th" ? "ปลดล็อก" : "Unlock"}
          </Link>
          <Button variant="outline" size="md" type="button">
            <Share2 className="h-4 w-4" aria-hidden />
            <span className={cn("hidden sm:inline")}>{locale === "th" ? "แชร์" : "Share"}</span>
          </Button>
        </div>
      </div>
    </section>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
